import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

// Computed
export const authComputed = {
  ...mapState('auth', ['currentUser', 'clientNavGroups', 'clientProfileLogo']),
  ...mapGetters('auth', ['loggedIn', 'isAdmin']),
};

export const errorsComputed = {
  ...mapState('errors', ['retry', 'serverError']),
};

export const userComputed = {
  ...mapState('user', ['userProfile', 'preferences']),
  ...mapGetters('user', ['onboardingComplete']),
};

export const macroCalculatorComputed = {
  ...mapState('macroCalculator', [
    'fat',
    'protein',
    'carbs',
    'weight',
    'height',
    'heightOptions',
    'bodyTypeOptions',
    'bodyTypeValue',
    'age',
    'targetWeight',
    'calories',
    'genderOptions',
    'genderSelectionIndex',
    'activityLevelOptions',
    'activityLevelSelectionIndex',
    'goalOptions',
    'goalSelectionIndex',
  ]),
  ...mapGetters('macroCalculator', ['heightInCm', 'weightInKg']),
};

export const dishesComputed = {
  ...mapState('dishes', ['dishes', 'filterOpts', 'toggleFilters', 'filterQueryString', 'isFavoritesVisible']),
  ...mapGetters('dishes', ['filterCount']),
};

export const groceryListComputed = {
  ...mapState('groceryList', ['groceryList', 'selectedItem', 'isListActive', 'isShowModal']),
};

export const primaryNavComputed = {
  ...mapState('primaryNav', ['primaryNavContent', 'activePrimaryNavIndex', 'activePrimaryNav']),
};

export const kitchenDrawerComputed = {
  ...mapState('kitchenDrawer', ['navContent', 'activeNavIndex', 'activeNav', 'isOpen', 'drawerTransitioning', 'drawerTransitionOrigin']),
};

export const mainComputed = {
  ...mapState('main', ['isLoading', 'loadingMessage', 'userAction']),
  ...mapGetters('main', ['lastScrollTop']),
};

export const mealsComputed = {
  ...mapGetters('meals', ['cachedMeal']),
};

export const onboardingComputed = {
  ...mapState('onboarding', ['isOnboarding', 'isOnboardingEnabled']),
};

export const planComputed = {
  ...mapState('plan', [
    'dailySummary',
    'selectedDaySlot',
    'weekOffset',
    'dishToAdd',
    'currentPlanBufferIndex',
    'changingWeeks',
    'isPlanEmpty',
    'isCalVisible',
    'otherDaySelected',
    'userRequestClearPlan',
  ]),
  ...mapGetters('plan', ['currentWeekString', 'previousPlan', 'currentPlan', 'nextPlan']),
};

export const platelistComputed = mapState('platelist', ['platelist', 'platelistDays', 'appliedPlatelist', 'selectedPlatelist']);

export const favoritesComputed = {
  ...mapState('favorites', ['favorites']),
  ...mapGetters('favorites', ['favoriteDishesMap']),
};

export const recipesComputed = {
  ...mapState('recipes', ['recipeDetailFull', 'recipeSource']),
  ...mapGetters('recipes', ['cachedRecipesMap']),
};

export const shareComputed = {
  ...mapState('share', ['copiedLink']),
};

// Methods
export const authApiMethods = mapActions('auth', ['logIn', 'logOut', 'signUp', 'resetPassword']);

export const errorsMethods = {
  ...mapMutations('errors', ['setRetry', 'setServerError']),
};

export const userMethods = {
  ...mapActions('user', ['getUserProfile', 'updateUserProfile', 'getPreferenceCategories', 'getPreferences', 'updatePreferences']),
  ...mapMutations('user', ['setPreferences', 'setPreferenceCategories']),
};

export const macroCalculatorMethods = {
  ...mapMutations('macroCalculator', [
    'setMacrosFat',
    'setMacrosProtein',
    'setMacrosCarbs',
    'setMacrosWeight',
    'setMacrosHeight',
    'setMacrosAge',
    'setMacrosCalories',
    'setMacrosTargetWeight',
    'setMacrosGenderSelectionIndex',
    'setMacrosBodyTypeValue',
    'setMacrosActivityLevelSelection',
    'setMacrosGoalSelectionIndex',
  ]),
};

export const dishesMethods = {
  ...mapMutations('dishes', ['setDishes', 'setToggleFilters', 'setFilterQueryString', 'initializeFilterQueryString', 'setIsFavoritesVisible']),
  ...mapActions('dishes', ['addDish', 'adjustLeftovers', 'getDishes', 'getFilterOpts', 'removeDish']),
};

export const recipesMethods = {
  ...mapMutations('recipes', ['setRecipeDetailFull', 'setRecipeSource']),
  ...mapActions('recipes', ['getRecipe', 'getPublicRecipe', 'getRecipeSourceHeaders']),
};

export const favoritesMethods = {
  ...mapActions('favorites', ['getFavorites', 'addFavorite', 'removeFavorite']),
};

export const primaryNavMethods = {
  ...mapMutations('primaryNav', ['setActivePrimaryNav', 'resetPrimaryStack', 'shiftPrimaryNavContent']),
  ...mapActions('primaryNav', ['navigateDeeperPrimary', 'navigateHigherPrimary']),
};

export const kitchenDrawerMethods = {
  ...mapMutations('kitchenDrawer', ['setActiveNav', 'keepNavStateButToggleDrawer', 'resetDrawer', 'setDrawerTransitionOrigin', 'shiftNavContent']),
  ...mapActions('kitchenDrawer', ['navigateDeeper', 'navigateHigher', 'toggleDrawer']),
};

export const groceryListMethods = {
  ...mapMutations('groceryList', [
    'setDeleteGroceryItem',
    'setDeleteGroceryStapleItem',
    'setSelectedItem',
    'setNewGroceryItem',
    'setUpdateGroceryItem',
    'setToggleList',
    'setShowModal',
  ]),
  ...mapActions('groceryList', [
    'getShoppingData',
    'getGroceryList',
    'addGroceryListItem',
    'addGroceryListStapleItem',
    'deleteGroceryListItem',
    'updateGroceryListItem',
    'shareGroceryList',
  ]),
};

export const mainMethods = {
  ...mapMutations('main', ['showLoader', 'hideLoader', 'updateLoadingText', 'setUserAction']),
};

export const mealsMethods = {
  ...mapActions('meals', ['getMeal']),
};

export const onboardingMethods = {
  ...mapMutations('onboarding', ['setIsOnboarding']),
};

export const planMethods = {
  ...mapMutations('plan', [
    'setDailySummary',
    'setSelectedDaySlot',
    'setWeekOffset',
    'setDishToAdd',
    'setIsPlanEmpty',
    'setIsCalVisible',
    'setOtherDaySelected',
    'setUserRequestClearPlan',
  ]),
  ...mapActions('plan', [
    'getPlanGrid',
    'getCurrentPlanGrid',
    'getPreviousPlanGrid',
    'getNextPlanGrid',
    'getPlanGridByWeekOffset',
    'getNextWeekPlanGrid',
    'getPreviousWeekPlanGrid',
    'clearPlan',
    'getPlannedDish',
    'reviseServes',
  ]),
};

export const platelistMethods = {
  ...mapMutations('platelist', ['setPlatelist', 'setPlatelistDays', 'setSelectedPlatelist', 'setUpdatePlatelistLockStatus', 'setAppliedPlatelist']),
  ...mapActions('platelist', ['getPlatelist', 'getPlatelistDays', 'getPublicPlatelistSummary', 'applyPlatelist', 'unlockPlatelist']),
};

export const shareMethods = {
  ...mapMutations('share', ['setShareableLink']),
  ...mapActions('share', ['generateMealShareUrl', 'generateRecipeShareUrl']),
};
